import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell, CalendarClock, CheckCheck, Droplets, FileWarning, FolderOpen, Landmark, Layers, X } from "lucide-react";
import { formatDate, practiceApi } from "../lib/practiceApi";
import "./notifications.css";

const ICONS = { pratiche: FolderOpen, catasto: Landmark, pozzi: Droplets, documenti: FileWarning, attivita: CalendarClock, altre: Layers };
const TONE = { scadute: "danger", urgenti: "warning", in_arrivo: "info", programmate: "info" };

const NotificationItem = ({ item, onOpen }) => {
  const Icon = ICONS[item.section] || Bell;
  return <button className={item.read ? "notif-item read" : "notif-item"} data-testid={`notification-item-${item.key}`} onClick={() => onOpen(item)}>
    <Icon size={16} className={`notif-icon ${item.severity}`} />
    <span>
      <strong>{item.title}</strong>
      <em>{[item.kind, item.source].filter(Boolean).join(" · ")}</em>
      <em className="notif-client">{item.client ? `Committente: ${item.client}` : ""}</em>
      <small>{[item.date && formatDate(item.date), item.days_label, item.status, item.assignee && `Assegnata a ${item.assignee}`].filter(Boolean).join(" · ")}</small>
    </span>
  </button>;
};

export const NotificationBell = ({ onOpenTarget }) => {
  const [data, setData] = useState({ items: [], unread: 0, total: 0, sections: [], buckets: [] });
  const [section, setSection] = useState("");
  const [bucket, setBucket] = useState("");
  const [open, setOpen] = useState(false);
  const panel = useRef(null);

  const load = useCallback(() => {
    practiceApi("/notifications").then(setData).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 120000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const close = event => { if (!panel.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const visible = data.items.filter(item => (!section || item.section === section) && (!bucket || item.bucket === bucket));

  async function markAll() {
    await practiceApi("/notifications/read", { method: "POST", body: JSON.stringify({ all: true }) }).catch(() => {});
    load();
  }

  async function openItem(item) {
    await practiceApi("/notifications/read", { method: "POST", body: JSON.stringify({ keys: [item.key] }) }).catch(() => {});
    setOpen(false);
    load();
    onOpenTarget(item.target);
  }

  return <div className="notif-wrap" ref={panel}>
    <button className="icon-button" data-testid="notifications-button" onClick={() => setOpen(o => !o)} aria-label="Notifiche">
      <Bell size={19} />
      {data.unread > 0 && <span className="notif-badge" data-testid="notifications-badge">{data.unread > 99 ? "99+" : data.unread}</span>}
    </button>

    {open && <section className="notif-panel" data-testid="notifications-panel">
      <header>
        <strong>Notifiche</strong>
        <span data-testid="notifications-summary">{data.total} aperte · {data.unread} da leggere</span>
        <button className="icon-btn" data-testid="notifications-close" onClick={() => setOpen(false)}><X size={14} /></button>
      </header>
      <div className="notif-filters">
        <div className="notif-chips" role="tablist">
          <button className={!section ? "on" : ""} data-testid="notifications-section-all" onClick={() => setSection("")}>Tutte <b>{data.total}</b></button>
          {data.sections.filter(s => s.count).map(s => <button key={s.key} className={section === s.key ? "on" : ""} data-testid={`notifications-section-${s.key}`} onClick={() => setSection(s.key)}>{s.label} <b>{s.count}</b></button>)}
        </div>
        <div className="notif-chips small">
          {data.buckets.map(bk => <button key={bk.key} className={bucket === bk.key ? `on ${TONE[bk.key]}` : ""} data-testid={`notifications-bucket-${bk.key}`} onClick={() => setBucket(bucket === bk.key ? "" : bk.key)}>{bk.label}</button>)}
        </div>
      </div>

      <div className="notif-list">
        {visible.length ? data.sections.map(sec => {
          const rows = visible.filter(item => item.section === sec.key);
          if (!rows.length) return null;
          return <div key={sec.key} className="notif-section" data-testid={`notifications-group-${sec.key}`}>
            <span className="notif-section-label">{sec.label} <b>{rows.length}</b></span>
            {data.buckets.map(bk => {
              const group = rows.filter(item => item.bucket === bk.key);
              if (!group.length) return null;
              return <div key={bk.key} className="notif-group">
                <span className={`notif-group-label ${TONE[bk.key]}`}>{bk.label} · {group.length}</span>
                {group.map(item => <NotificationItem key={item.key} item={item} onOpen={openItem} />)}
              </div>;
            })}
          </div>;
        }) : <div className="notif-empty" data-testid="notifications-empty"><CheckCheck size={26} /><strong>Tutto in ordine</strong><span>Nessuna scadenza in questa vista.</span></div>}
      </div>

      {!!data.items.length && <footer>
        <button data-testid="notifications-mark-all" onClick={markAll}><CheckCheck size={14} />Segna tutte come lette</button>
      </footer>}
    </section>}
  </div>;
};
